import React, {FormEvent, useEffect, useRef} from "react";
import {observer} from "mobx-react-lite";
import {getSnapshot} from "mobx-state-tree";
import intl from "react-intl-universal";
import {useAppState, useUserState} from "@stores/AppStateContext";

import {InputWithClear} from "../../../components/InputWithClear"
import {SideModal} from "../components/SideModal/SideModal";
import {SideModalContentBlock} from "../components/SideModal/SideModalContentBlock";
import {Button} from "../../../components/Button";
import {InfoBlockPlain} from "../../../components/InfoBlockRow/InfoBlockPlain";

import {MODALS, TaskListEditProps} from "../modal-names";
import {TaskListModel, TaskListModelType} from "../../../../stores/taskLists/TaskListModel";

export const TaskListEdit = observer((
    {taskListUnitModel, taskListId}: TaskListEditProps
) => {
    const appState = useAppState();
    const userState = useUserState();
    const inputRef = useRef<HTMLInputElement>(null);
    const currentUser = userState!.currentUserDb!;

    const taskListModelRef = useRef<TaskListModelType | undefined>(
        taskListId
            ? taskListUnitModel.taskLists.get(taskListId)
            : TaskListModel.create({
                name: '',
                createdBy: currentUser?.id,
                updatedBy: currentUser?.id,
            })
    );

    useEffect(() => {
        if (!taskListModelRef.current) {
            console.error(`tasklist with id${taskListId} not found`);
            appState.setModal(null);
        }
    }, [])

    useEffect(() => {
        const id = requestAnimationFrame(() => {
            inputRef.current?.focus();
        });
        return () => cancelAnimationFrame(id);
    }, []);

    if (taskListModelRef.current === undefined) {
        return null;
    }

    const onSubmit = (event: FormEvent) => {
        event.preventDefault();
        const today = new Date();
        if (!taskListId) {
            taskListModel.setCreatedAt(today.toISOString());
        }
        taskListModel.setUpdatedAt(today.toISOString());
        taskListModel.setUpdatedBy(currentUser.id);

        taskListUnitModel.setTaskList(getSnapshot(taskListModel));
        taskListUnitModel.save();
        appState.setModal(null);
    }

    const taskListModel = taskListModelRef.current;

    return (
        <SideModal
            header={(
                taskListId
                    ? intl.get('taskList.edit-list').d('List Settings')
                    : intl.get('taskList.create=list').d('Create List')
            )}
            open={true}
        >
            <form
                onSubmit={onSubmit}
            >
                <SideModalContentBlock
                    header={intl.get('taskList.list-name').d('List Name')}
                    description={intl.get('taskList.list-name.hint').d('Enter a name to group related tasks together.')}
                    mainContent={(
                        <InputWithClear
                            ref={inputRef}
                            withBackground={true}
                            Icon={null}
                            placeholder={intl.get('taskList.list-name.placeholder').d('e.g. “Morning Tasks”, “Opening tasks”, “Monthly Checks”')}
                            value={taskListModel.name}
                            onChange={taskListModel.setName}
                            isRequired={true}
                        />
                    )}
                    infoText={intl.get('taskList.list-name.info').d('This name will be visible as a section title in your task table.')}
                />
                <div className="TaskEdit__buttons">
                    <Button
                        text={(
                            taskListId
                                ? intl.get('common.save').d("Save")
                                : intl.get('taskList.create-btn-text').d("Create Task List")
                        )}
                        style={"solid"}
                        type={"primary"}
                        htmlType={'submit'}
                    />
                    {
                        taskListId && (
                            <Button
                                className={"SideModal__deleteButton"}
                                text={intl.get('taskList.delete.button').d("Delete Tasks List")}
                                style={"outlined"}
                                type={"danger"}
                                onClick={() => {
                                    appState.setModal({
                                        name: MODALS.TASK_LIST_DELETE,
                                        params: {
                                            taskListModel,
                                            taskListUnitModel
                                        }
                                    });
                                }}
                            />
                        )
                    }
                </div>
                {
                    taskListId && (
                        <InfoBlockPlain
                            className="TaskEdit__info"
                            createdBy={taskListModel.createdBy}
                            createdAt={taskListModel.createdAt}
                            updatedBy={taskListModel.updatedBy}
                            updatedAt={taskListModel.updatedAt}
                        />
                    )
                }
            </form>
        </SideModal>
    )
})
